import { clsx } from 'clsx';
import { format, isSameDay } from 'date-fns';
import { useMemo } from 'react';
import { Content, useFormattedContent } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { InfoOutlineIcon, WarningOutlineIcon } from 'src/svgs/Viagogo';
import { EVENT_STATUS_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getLocaleFromLanguageOrCurrent } from 'src/utils/localeUtils';
import { ApiEventStatus, Feature } from 'src/WebApiController';

import {
  EventStatusProps,
  EventStatusToColor,
  StatusColor,
} from './EventStatus.type';
import * as styles from './EventStatusBanner.css';

const DateRelatedStatuses: ApiEventStatus[] = [ApiEventStatus.Rescheduled];

export const EventStatusBanner = ({
  status,
  originalStartDate,
  currentStartDate,
}: EventStatusProps) => {
  const hasShowOriginalStartDate = useUserHasFeature(
    Feature.ShowEventOriginalStartDate
  );

  const showRescheduledDate = useMemo(() => {
    if (
      !hasShowOriginalStartDate ||
      !originalStartDate ||
      !status ||
      !DateRelatedStatuses.includes(status)
    ) {
      return false;
    }
    // Don't show the original date suffix when only the time changed, not the day
    if (
      currentStartDate &&
      isSameDay(new Date(originalStartDate), new Date(currentStartDate))
    ) {
      return false;
    }
    return true;
  }, [hasShowOriginalStartDate, originalStartDate, currentStartDate, status]);

  const formattedOriginalDate = useMemo(() => {
    if (!showRescheduledDate || !originalStartDate) {
      return '';
    }
    const locale = getLocaleFromLanguageOrCurrent();
    return format(new Date(originalStartDate), 'MMM d, y', { locale });
  }, [showRescheduledDate, originalStartDate]);

  const rescheduledFromDateText = useFormattedContent(
    FormatContentId.RescheduledFromDate,
    formattedOriginalDate
  );

  // Don't show normal state
  if (!status || status === ApiEventStatus.Normal) {
    return null;
  }

  const colorStat = EventStatusToColor[status];
  return (
    <Stack
      className={clsx(
        styles.eventStatusBannerWrapper,
        styles.eventStatusBannerBorder[colorStat]
      )}
      direction="row"
      alignItems="center"
      justifyContent="center"
      gap="m"
    >
      <Stack alignItems="center" justifyContent="center">
        {colorStat === StatusColor.Gray ? (
          <InfoOutlineIcon fill="inherit" size={vars.iconSize.m} />
        ) : (
          <WarningOutlineIcon fill="inherit" size={vars.iconSize.m} />
        )}
      </Stack>
      <span className={styles.eventStatusBannerColor[colorStat]}>
        {showRescheduledDate ? (
          rescheduledFromDateText
        ) : (
          <Content id={EVENT_STATUS_TO_CID[status]} />
        )}
      </span>
    </Stack>
  );
};
